import { z } from 'zod';
import type { ClientType, LegalEntityType, LoyaltyStatus } from '@/types/entities/client';

export const clientFormSchema = z.object({
  // Добавляем ключевое поле type
  type: z.enum(['individual', 'legal']) as z.ZodType<ClientType>,

  // Общие поля в camelCase
  fullName: z.string().optional(),
  phone: z.string().min(1, 'Телефон обязателен для заполнения'),
  email: z.string().email('Некорректный email').optional().or(z.literal('')),
  address: z.string().optional(),
  comment: z.string().optional(),
  
  // Поля для физических лиц в camelCase
  birthDate: z.string().optional(),
  remindBirthday: z.boolean().default(false),
  remindDaysBefore: z.number().min(1).max(30).default(3),
  passportSeries: z.string().optional(),
  passportNumber: z.string().optional(),
  passportSubdivisionCode: z.string().optional(),
  passportIssuedBy: z.string().optional(),
  passportIssueDate: z.string().optional(),
  
  // Поля для юридических лиц в camelCase
  companyName: z.string().optional(),
  inn: z.string().optional(),
  kpp: z.string().optional(),
  legalEntityType: z
    .preprocess((val) => {
      if (val === null || val === undefined || val === '') return null;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        if (v === 'ип') return 'ip';
        if (v === 'ооо') return 'ooo';
        if (v === 'ip' || v === 'ooo') return v;
      }
      return val;
    }, z.enum(['ip', 'ooo']).nullable())
    .optional() as z.ZodType<LegalEntityType | undefined>,
  bankName: z.string().optional(),
  bankAccount: z.string().optional(),
  bankBik: z.string().optional(),
  // Новые поля для юридических лиц
  bankCorrespondentAccount: z.string().optional(), // Корреспондентский счёт
  directorTitle: z.string().optional(), // Статус руководителя (директор, ген.директор, ИП и т.д.)
  directorName: z.string().optional(), // ФИО руководителя
  legalBasis: z.string().optional(), // Основание (Устав, ОГРНИП № и т.д.)
  
  // Статус и лояльность
  loyaltyStatus: z.enum(['white', 'gray', 'black']).optional() as z.ZodType<LoyaltyStatus | undefined>,
  status: z.enum(['active', 'inactive']).optional(),
}).refine((data) => {
  // Валидация в зависимости от типа клиента будет происходить в компоненте
  return true;
}, {
  message: "Заполните обязательные поля"
});

export type ClientFormData = z.infer<typeof clientFormSchema>;

// Функции для валидации отдельных полей (обновлены на camelCase)
export const validateIndividualClient = (data: ClientFormData) => {
  const errors: Record<string, string> = {};
  
  if (!data.fullName?.trim()) {
    errors.fullName = 'ФИО обязательно для заполнения';
  }
  
  if (!data.phone?.trim()) {
    errors.phone = 'Телефон обязателен для заполнения';
  }
  
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Некорректный email';
  }
  
  if (data.passportSeries && data.passportSeries.length !== 4) {
    errors.passportSeries = 'Серия паспорта должна содержать 4 цифры';
  }
  
  if (data.passportNumber && data.passportNumber.length !== 6) {
    errors.passportNumber = 'Номер паспорта должен содержать 6 цифр';
  }
  
  return errors;
};

export const validateLegalClient = (data: ClientFormData) => {
  const errors: Record<string, string> = {};
  
  if (!data.companyName?.trim()) {
    errors.companyName = 'Название компании обязательно для заполнения';
  }
  
  if (!data.phone?.trim()) {
    errors.phone = 'Телефон обязателен для заполнения';
  }
  
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Некорректный email';
  }
  
  if (data.inn && (data.inn.length !== 10 && data.inn.length !== 12)) {
    errors.inn = 'ИНН должен содержать 10 или 12 цифр';
  }
  
  if (data.kpp && data.kpp.length !== 9) {
    errors.kpp = 'КПП должен содержать 9 цифр';
  }
  
  if (data.bankBik && data.bankBik.length !== 9) {
    errors.bankBik = 'БИК должен содержать 9 цифр';
  }
  
  return errors;
};
